import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Switch,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { CustomInput } from "global/components";
import SettingsSearchField from "../components/SettingsSearchField";
import AppModal from "global/components/AppModal/AppModal";
import EmptyState from "global/components/EmptyState/EmptyState";
import FeatureBanner from "global/components/FeatureBanner/FeatureBanner";
import SettingsLayout from "../components/SettingsLayout";
import { useTitle } from "utils/UseTitle";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectActiveWorkspace, setActiveWorkspace } from "redux/authSlice";
import { selectFeaturesLoaded } from "redux/featuresSlice";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { Workspace, WorkspaceQuota } from "types/workspace";
import { createWorkspace, fetchWorkspaces } from "./WorkspacesServices";
import { fetchWorkspaceQuotas } from "./WorkspaceQuotaServices";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import WorkspaceListSkeleton from "./components/WorkspaceListSkeleton";
import {
  AddSubWorkspaceDialog,
  MoveWorkspaceDialog,
} from "./components/WorkspaceTreeDialogs";
import { pathOf, toTree, WorkspaceNode } from "./workspaceTree";
import { notifyWorkspacesChanged } from "./workspaceEvents";
import workspacesTokens from "./Workspaces.tokens";
import {
  actionCellSx,
  cardSx,
  columnHeaderSx,
  headerActionsSx,
  headerToggleSx,
  listBannerSx,
  listSearchSx,
  nameButtonSx,
  nameCellSx,
  pillSx,
  primaryButtonSx,
  rowActionSx,
  rowTextSx,
  treeNameCellSx,
  treeRowSx,
  treeToggleSpacerSx,
  treeToggleSx,
  visuallyHiddenSx,
} from "./Workspaces.styles";

const { pill } = workspacesTokens;

/** `quotas` is null while they are still loading, so "No quota" never shows before the real numbers. */
const quotaLabel = (quotas: Record<string, WorkspaceQuota> | null, id: string) => {
  if (quotas === null) {
    return <Skeleton variant="text" width={56} aria-label="Loading envelope quota" />;
  }
  const q = quotas[id];
  if (!q || !q.eligible || typeof q.quota !== "number") {
    return (
      <Box component="span" sx={rowTextSx("body", "regular")}>
        No quota
      </Box>
    );
  }
  // Each workspace's quota is its own: the tree plays no part in it.
  // The numbers are plain row text; a used-up quota adds a status pill, like Active / Default on the name.
  return (
    <Box sx={nameCellSx}>
      <Box component="span" sx={rowTextSx("body", "regular")}>
        {`${q.used.toLocaleString()} / ${q.quota.toLocaleString()}`}
      </Box>
      {(q.remaining ?? 0) <= 0 && (
        <Box component="span" sx={pillSx(pill.danger)}>
          Full
        </Box>
      )}
    </Box>
  );
};

const Workspaces = () => {
  useTitle("Workspaces");
  const dispatch = useAppDispatch();
  const activeWorkspaceId = useAppSelector(selectActiveWorkspace);
  const isAdmin = hasAccessTo(strings.resources.app, strings.adminPermission);
  // Only account admins see the envelope quota column.
  const GRID = isAdmin
    ? workspacesTokens.list.grid.workspacesWithQuota
    : workspacesTokens.list.grid.workspaces;
  // Workspaces is a paid-plan feature. When the account's plan doesn't include it (trial),
  // the page stays in place, disabled, under the compact upgrade banner.
  const workspacesFeature = useFeatureState(FeatureKeys.WORKSPACES);
  const workspacesAvailable =
    workspacesFeature.status === strings.FEATURE_STATUS_AVAILABLE;
  // useFeatureState fail-opens to "available" before account access has loaded (so gated UI
  // doesn't flash on first paint), so workspacesAvailable alone isn't enough to gate the fetch --
  // it would still fire once on mount for a not-in-plan account, before the real status resolves.
  // Wait for the real entitlement to be known before ever calling the workspaces API.
  const featuresLoaded = useAppSelector(selectFeaturesLoaded);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [quotas, setQuotas] = useState<Record<string, WorkspaceQuota> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [nameInput, setNameInput] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuWs, setMenuWs] = useState<Workspace | null>(null);

  // Tree view: a collapsed workspace hides its sub-workspaces; a search shows every match with
  // the workspaces above it, whatever is collapsed.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState<string>("");
  const [addUnder, setAddUnder] = useState<Workspace | null>(null);
  const [moving, setMoving] = useState<Workspace | null>(null);

  // The quota column is extra information: if it cannot load, the list still shows.
  // Only account admins can read every workspace's quota (the column is hidden for others).
  const loadQuotas = async () => {
    if (!isAdmin) return;
    setQuotas(null);
    try {
      const list = await fetchWorkspaceQuotas();
      const byId: Record<string, WorkspaceQuota> = {};
      (Array.isArray(list) ? list : []).forEach((q) => {
        byId[q.workspaceId] = q;
      });
      setQuotas(byId);
    } catch {
      setQuotas({});
    }
  };

  const load = async () => {
    // Fetched side by side with the list, not after it.
    loadQuotas();
    try {
      setLoading(true);
      const data = await fetchWorkspaces(includeArchived);
      setWorkspaces(Array.isArray(data) ? data : []);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (featuresLoaded && workspacesAvailable) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived, featuresLoaded, workspacesAvailable]);

  const openCreate = () => {
    setNameInput("");
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    const name = nameInput.trim();
    if (!name) return;
    try {
      setSubmitting(true);
      await createWorkspace(name);
      openSuccessNotification(`Workspace "${name}" created.`);
      notifyWorkspacesChanged();
      setCreateOpen(false);
      setNameInput("");
      await load();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, ws: Workspace) => {
    setMenuAnchor(event.currentTarget);
    setMenuWs(ws);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuWs(null);
  };

  const go = (suffix: string) => {
    if (!menuWs) return;
    const id = menuWs.id;
    closeMenu();
    history.push(`${viewpaths.workspacesViewPath}/${id}/${suffix}`);
  };

  const rolesWorkspace =
    workspaces.find((ws) => ws.isDefault && !ws.archived) ??
    workspaces.find((ws) => !ws.archived) ??
    null;

  const openRoles = () => {
    if (!rolesWorkspace) return;
    history.push(`${viewpaths.workspacesViewPath}/${rolesWorkspace.id}/roles`);
  };

  const openMembers = (ws: Workspace) => {
    history.push(`${viewpaths.workspacesViewPath}/${ws.id}/members`);
  };

  const setActive = () => {
    if (!menuWs) return;
    const next = activeWorkspaceId === menuWs.id ? "" : menuWs.id;
    const name = menuWs.name;
    dispatch(setActiveWorkspace(next));
    closeMenu();
    openSuccessNotification(
      next ? `"${name}" is now your active workspace.` : "Active workspace cleared.",
    );
  };

  // Account admins see every workspace; a delegated admin sees the subtree they manage. Only
  // account admins add top-level workspaces and manage the account-wide roles.
  const headerAction = (
    <Box sx={headerActionsSx}>
      <FormControlLabel
        sx={headerToggleSx}
        disabled={!workspacesAvailable}
        control={
          <Switch
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
        }
        label="Include archived"
      />
      <Button
        variant="outlined"
        startIcon={<GroupsOutlinedIcon />}
        onClick={() => history.push(viewpaths.workspaceEnrollmentViewPath)}
        disabled={!workspacesAvailable}
        sx={primaryButtonSx}
      >
        Enrollment
      </Button>
      {isAdmin && (
        /*
          Roles are account-wide, so they're managed from here rather than from
          one workspace's menu. The route still carries a workspace id (the roles
          page activates it for the request context), so it opens through the
          Default workspace.
        */
        <Button
          variant="outlined"
          startIcon={<AdminPanelSettingsOutlinedIcon />}
          onClick={openRoles}
          disabled={!workspacesAvailable || !rolesWorkspace}
          sx={primaryButtonSx}
        >
          Manage roles
        </Button>
      )}
      {isAdmin && (
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={openCreate}
          disabled={!workspacesAvailable}
          sx={primaryButtonSx}
        >
          New workspace
        </Button>
      )}
    </Box>
  );

  const searching = search.trim() !== "";

  const toggleCollapsed = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** The rows to show: tree order, minus collapsed branches; a search keeps matches and their parents. */
  const visibleNodes = (): WorkspaceNode[] => {
    const nodes = toTree(workspaces);
    if (searching) {
      const text = search.trim().toLowerCase();
      const keep = new Set<string>();
      workspaces
        .filter((w) => w.name.toLowerCase().includes(text))
        .forEach((w) => pathOf(workspaces, w.id).forEach((p) => keep.add(p.id)));
      return nodes.filter((n) => keep.has(n.workspace.id));
    }
    const hidden = new Set<string>();
    return nodes.filter((n) => {
      const parent = n.workspace.parentId;
      if (parent && (collapsed.has(parent) || hidden.has(parent))) {
        hidden.add(n.workspace.id);
        return false;
      }
      return true;
    });
  };

  const getRow = (node: WorkspaceNode, isLast: boolean) => {
    const { workspace: ws, level, childCount } = node;
    const isActive = activeWorkspaceId === ws.id;
    const memberCount = ws.memberCount ?? 0;
    const open = searching || !collapsed.has(ws.id);
    return (
      <Box key={ws.id} sx={treeRowSx(GRID, isLast, level)}>
        <Box sx={treeNameCellSx(level)}>
          {childCount > 0 ? (
            <IconButton
              size="small"
              aria-label={`${open ? "Collapse" : "Expand"} ${ws.name}`}
              aria-expanded={open}
              onClick={() => toggleCollapsed(ws.id)}
              disabled={searching}
              sx={treeToggleSx}
            >
              {open ? (
                <ExpandMoreRoundedIcon fontSize="small" />
              ) : (
                <ChevronRightRoundedIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Box component="span" sx={treeToggleSpacerSx} />
          )}
          <Box sx={nameCellSx}>
            <Box
              component="button"
              type="button"
              onClick={() => openMembers(ws)}
              sx={nameButtonSx}
            >
              {ws.name}
            </Box>
            {isActive && (
              <Box component="span" sx={pillSx(pill.brand)}>
                Active
              </Box>
            )}
            {ws.isDefault && (
              <Box component="span" sx={pillSx(pill.neutral)}>
                Default
              </Box>
            )}
            {ws.archived && (
              <Box component="span" sx={pillSx(pill.warning)}>
                Archived
              </Box>
            )}
          </Box>
        </Box>
        <Box component="span" sx={rowTextSx("body", "regular")}>
          {`${memberCount} member${memberCount === 1 ? "" : "s"}`}
        </Box>
        {isAdmin && quotaLabel(quotas, ws.id)}
        <Box sx={actionCellSx}>
          <IconButton
            aria-label={`Actions for ${ws.name}`}
            aria-haspopup="menu"
            aria-expanded={menuWs?.id === ws.id}
            onClick={(e) => openMenu(e, ws)}
            sx={rowActionSx}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderRows = () => {
    if (loading) {
      return (
        <WorkspaceListSkeleton
          columns={GRID}
          middleCells={isAdmin ? ["30%", "40%"] : ["30%"]}
        />
      );
    }
    if (workspaces.length === 0) {
      return (
        <EmptyState
          icon="folder"
          title={includeArchived ? "No workspaces" : "No workspaces yet"}
          body="Workspaces keep each team's requests, templates and members apart."
          primaryAction={
            isAdmin && workspacesAvailable
              ? { label: "New workspace", onClick: openCreate }
              : undefined
          }
        />
      );
    }
    const rows = visibleNodes();
    if (rows.length === 0) {
      return (
        <EmptyState
          icon="folder"
          title="No matching workspaces"
          body={`No workspace name contains "${search.trim()}".`}
        />
      );
    }
    return rows.map((node, index) => getRow(node, index === rows.length - 1));
  };

  const menuActive = menuWs && activeWorkspaceId === menuWs.id;

  return (
    <SettingsLayout activeTab="Workspaces" title="Workspaces" headerAction={headerAction}>
      <Box sx={cardSx}>
        {!workspacesAvailable && (
          <Box sx={listBannerSx}>
            <FeatureBanner
              state={workspacesFeature}
              featureKey={FeatureKeys.WORKSPACES}
              featureName="Workspaces"
              variant="compact"
            />
          </Box>
        )}
        {workspaces.length > 1 && (
          <Box sx={listSearchSx}>
            {/* The shared Settings search box (Teams, Roles); filters the list as you type. */}
            <SettingsSearchField
              id="workspace_search_box"
              placeholder="Search workspaces"
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
        )}
        <Box sx={columnHeaderSx(GRID)}>
          <Box component="span">Name</Box>
          <Box component="span">Members</Box>
          {isAdmin && (
            <Tooltip
              arrow
              title="Used / quota this billing period. Each workspace has its own quota: sub-workspaces don't count toward their parent's."
            >
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                Envelope quota
                <InfoOutlinedIcon sx={{ fontSize: 14 }} aria-label="About envelope quota" />
              </Box>
            </Tooltip>
          )}
          <Box component="span" sx={visuallyHiddenSx}>
            Actions
          </Box>
        </Box>
        <Box aria-busy={loading}>{renderRows()}</Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{
          "aria-label": menuWs ? `Actions for ${menuWs.name}` : "Workspace actions",
        }}
      >
        <MenuItem onClick={setActive}>
          {menuActive ? "Clear active workspace" : "Switch to this workspace"}
        </MenuItem>
        <MenuItem onClick={() => go("members?invite=1")}>Invite users</MenuItem>
        <MenuItem onClick={() => go("members")}>Manage team</MenuItem>
        <MenuItem onClick={() => go("details")}>Workspace details</MenuItem>
        <MenuItem onClick={() => go("branding")}>Branding</MenuItem>
        {menuWs?.canAddChild && (
          <MenuItem
            onClick={() => {
              const parent = menuWs;
              closeMenu();
              setAddUnder(parent);
            }}
          >
            Add sub-workspace
          </MenuItem>
        )}
        {menuWs?.canMove && (
          <MenuItem
            onClick={() => {
              const target = menuWs;
              closeMenu();
              setMoving(target);
            }}
          >
            Move
          </MenuItem>
        )}
      </Menu>

      <AddSubWorkspaceDialog parent={addUnder} onClose={() => setAddUnder(null)} onCreated={load} />
      <MoveWorkspaceDialog
        workspace={moving}
        all={workspaces}
        onClose={() => setMoving(null)}
        onMoved={load}
      />

      <AppModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New workspace"
        subtitle="Give the team's workspace a name. You can rename it later."
        size="sm"
        busy={submitting}
        primaryLabel="Create workspace"
        onPrimary={submitCreate}
        primaryLoading={submitting}
        primaryDisabled={!nameInput.trim()}
        cancelLabel="Cancel"
      >
        <CustomInput
          autoFocus
          label="Workspace name"
          placeHolder="Enter workspace name"
          type="text"
          name="workspaceName"
          value={nameInput}
          onChange={(e: any) => setNameInput(e.target.value)}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") submitCreate();
          }}
        />
      </AppModal>
    </SettingsLayout>
  );
};

export default Workspaces;
